import { ArchiveIcon, ChartBarIcon, FlagIcon, PencilSimpleIcon, PlusIcon, SlidersHorizontalIcon, TrashIcon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { day, metricValue } from "./format.ts";
import { collectedUnit, METRIC_DASHBOARD_LABELS as D, METRIC_LABELS as L, METRIC_MANAGEMENT_LABELS as N } from "./labels.ts";
import { type MetricPanel, useMetricActions } from "./metric-actions-context.ts";
import { type CardNote, cardBars, cardNotes, definitionWarning, direction, metricName, missingFix, type Metric } from "./metric-dashboard.ts";
import { MoreMenu } from "./ui.tsx";
import { MetricCard, type MiniBar, MiniBars } from "./ui/measure.tsx";
import { Chip } from "./ui/status.tsx";

export function MetricMenu({ metric: m, primary = false }: { metric: Metric; primary?: boolean }) {
  const actions = useMetricActions();
  const open = (panel: Parameters<typeof actions.open>[1]) => () => actions.open(m.definition.key, panel);
  return <MoreMenu label={`${N.more} · ${metricName(m)}`} items={[
    { label: N.view, icon: <ChartBarIcon size={16} />, onSelect: open("trend") },
    { label: N.edit, icon: <PencilSimpleIcon size={16} />, onSelect: open("edit") },
    { label: N.newVersion, icon: <SlidersHorizontalIcon size={16} />, disabled: Boolean(m.presentation?.archived_at), onSelect: open("version") },
    { label: primary ? N.changePrimary : N.choosePrimary, icon: <FlagIcon size={16} />, onSelect: actions.primary },
    { label: N.pin, icon: <PlusIcon size={16} />, disabled: Boolean(m.presentation?.archived_at), onSelect: open("pin") },
    "sep",
    { label: m.presentation?.archived_at ? N.restore : N.archive, icon: <ArchiveIcon size={16} />, onSelect: open("archive") },
    { label: N.delete, icon: <TrashIcon size={16} />, danger: true, onSelect: open("delete") },
  ]} />;
}

/** A metric's unit as written: the display unit a person set, else the definition's, with zooi's collected units in the interface language. */
export const displayUnit = (m: Pick<Metric, "definition" | "presentation">, value: number | null = null) => m.presentation?.unit ?? collectedUnit(m.definition.unit, value);

/** The chip beside a metric's name: archived, the product's primary metric, or the first metric of running projects. */
export function metricTag(m: Metric, primary: boolean) {
  return m.presentation?.archived_at ? <Chip>{N.archived}</Chip> : primary ? <Chip tone="accent" variant="soft">{N.productPrimary}</Chip> : m.primary_for.length > 0 ? <span title={D.primaryFor(m.primary_for.map(c => c.title))}><Chip>{N.projectPrimary}</Chip></span> : undefined;
}

/** Each period as a small bar, labelled with its date and value; a period still open says so. */
export function metricBars(m: Metric, periods: C.MetricDashboard["periods"]): MiniBar[] {
  return cardBars(m, periods).map((b, i) => {
    const point = m.points[i], open = periods[i]?.in_progress || b.state === "pending";
    const note = open ? D.notYet : point && point.days_with_data < point.days ? D.coverage(point.days_with_data, point.days) : null;
    return { value: b.value, state: b.state, label: [day(b.start), b.value === null ? "–" : metricValue(b.value, m.definition.kind), note].filter(Boolean).join(" · ") };
  });
}

const noteText = (note: CardNote) => note.kind === "coverage" ? N.cardCoverage(note.observed, note.days) : note.kind === "through" ? N.through(day(note.date)) : note.kind === "zone" ? note.zone : D.smallSample;

/**
 * Shared complete-day card. Today's partial preview never replaces its value.
 * With `periods` it draws each period as a small bar; with `page` it leaves
 * out what the page already says (the product's time zone, the range's end).
 */
export function MetricOverviewCard({ metric: m, productId, primary = false, trend = true, onOpen, onSource, menu = true, periods, page }: { metric: Metric; productId: string; primary?: boolean; trend?: boolean; onOpen?: (panel?: MetricPanel) => void; onSource?: () => void; menu?: boolean; periods?: C.MetricDashboard["periods"]; page?: { timeZone: string; end: string | null } }) {
  const actions = useMetricActions();
  const d = m.definition, r = m.range;
  const moved = r.small_sample ? direction(r) : null, fix = missingFix(m);
  const archived = Boolean(m.presentation?.archived_at);
  const fullPeriodDays = Math.max(0, ...m.points.map(p => p.days));
  const trendPoints = m.points.map(p => p.days < fullPeriodDays || p.days_with_data < p.days ? null : p.value);
  const daily = m.aggregation === "daily_unique_sum";
  const unit = d.kind === "rate" ? undefined : daily ? `${displayUnit(m, r.value)} · ${N.dailyTotal}` : displayUnit(m, r.value);
  const notes = cardNotes(m, page ?? { timeZone: d.time_zone, end: null });
  const showTrend = !archived && trend;
  // A metric without a single observed period has nothing to draw; its card says what is missing.
  const bars = showTrend && periods && m.points.some(p => p.value !== null) ? metricBars(m, periods) : null;
  return <MetricCard label={metricName(m)} unit={unit} unitNote={daily ? N.dailyUnique : undefined}
    tag={metricTag(m, primary)}
    actions={menu && <MetricMenu metric={m} primary={primary} />}
    value={r.value === null ? null : metricValue(r.value, d.kind)} onOpen={() => onOpen ? onOpen("trend") : actions.open(d.key)}
    delta={archived ? null : r.small_sample ? moved && D.direction[moved](r.days) : r.change === null ? null : L.change(r.change, r.change_unit)}
    chart={bars ? <MiniBars bars={bars} label={`${metricName(m)}: ${bars.map(b => b.label).join("; ")}`} /> : undefined}
    trend={showTrend && !periods ? trendPoints : undefined} trendLabel={metricName(m)}
    sub={<>
      {r.value === null && <span>{m.missing ? D.missing[m.missing] : N.noValues}{fix && <> · <MissingFix metric={m} productId={productId} onImport={() => onOpen ? onOpen("import") : actions.open(d.key, "import")} onSource={onSource} /></>}</span>}
      {notes.length > 0 && <span>{notes.map(noteText).join(" · ")}</span>}
      {definitionWarning(m) && <button className="z-link z-linkbtn" type="button" onClick={() => onOpen ? onOpen("definition") : actions.open(d.key, "definition")}>{N.reviewDefinition}</button>}
    </>}
  />;
}

/** How a missing number is added: an import for a person's own data, the data sources for collected data. */
export function MissingFix({ metric: m, productId, onImport, onSource }: { metric: Metric; productId: string; onImport: () => void; onSource?: () => void }) {
  const fix = missingFix(m);
  if (!fix) return null;
  return fix === "import" ? <button className="z-link z-linkbtn" type="button" onClick={onImport}>{D.fix.import}</button>
    : onSource ? <button className="z-link z-linkbtn" type="button" onClick={onSource}>{D.fix[fix]}</button>
    : <Link className="z-link" to="/p/$productId/data" params={{ productId }} search={{ view: "sources" }}>{D.fix[fix]}</Link>;
}
